/**
 * Gravity field view: a 2D "green map" of the system's gravity.
 *
 * Like a putting-green read, the view is a flat surface you take in at a
 * glance. The system is projected onto its best-fit plane and drawn as:
 *   - a heat map of pull strength — flat (calm, low pull) is green, steep
 *     (strong pull, near a planet) runs blue → orange;
 *   - contour lines of the gravitational potential, the integrated shape of all
 *     the wells together, so saddles and ridges between planets are visible;
 *   - sparse break arrows showing which way a shot drifts in that region;
 *   - ring markers at the low-gravity saddles (the Lagrange-like pockets).
 *
 * Nothing animates. The map is rebuilt only when planets change (mass,
 * position, alive), so a crater visibly flattens the well around the planet
 * that lost mass. Rendering only: reads planets, writes nothing. `quality` 0–1
 * sets grid resolution and arrow density for the eventual player version.
 */

import * as THREE from 'three';
import type { PlanetState, Vec3 } from '../types';
import { gravityAccel } from '../core/physics';
import { G } from '../constants';

export interface GravityField {
  group: THREE.Group;
  quality: number;
  planetKey: string;
  /** The plane the map lies on (set on build). */
  plane: Plane | null;
  /** The aim preview's shadow on the plane, when a shot is being aimed. */
  previewLine: THREE.Line | null;
  previewImpact: THREE.Mesh | null;
}

// ---- tuning ---------------------------------------------------------------
const A_WEAK = 0.03;        // |a| at the bottom of the colour ramp
const A_STRONG = 8;         // |a| at the top
const CONTOUR_LEVELS = 12;
const HEAT_ALPHA = 0.42;
const CONTOUR_ALPHA = 0.32;
const ARROW_ALPHA = 0.9;
const SADDLE_MARKERS_MAX = 8;

const FLAT = new THREE.Color(0.30, 0.85, 0.50);   // calm — low pull
const MID = new THREE.Color(0.20, 0.38, 0.85);    // moderate
const STEEP = new THREE.Color(1.00, 0.45, 0.20);  // strong pull
const _c = new THREE.Color();

function rampColor(t: number, out: THREE.Color): THREE.Color {
  if (t < 0.5) return out.copy(FLAT).lerp(MID, t * 2);
  return out.copy(MID).lerp(STEEP, (t - 0.5) * 2);
}

function planetKeyOf(planets: PlanetState[]): string {
  let k = '';
  for (const p of planets) k += `${p.id}:${p.alive ? 1 : 0}:${p.mass.toFixed(2)}:${p.position.x.toFixed(1)},${p.position.y.toFixed(1)},${p.position.z.toFixed(1)}|`;
  return k;
}

// ---- plane fit ------------------------------------------------------------

interface Plane { center: THREE.Vector3; u: THREE.Vector3; v: THREE.Vector3; n: THREE.Vector3; extent: number }

/** Best-fit plane through the live planets (PCA normal = least-spread direction); XZ when there are too few. */
function fitPlane(planets: PlanetState[]): Plane {
  const live = planets.filter(p => p.alive && p.type !== 'wormhole');
  const center = new THREE.Vector3();
  for (const p of live) center.add(new THREE.Vector3(p.position.x, p.position.y, p.position.z));
  if (live.length > 0) center.divideScalar(live.length);

  let n = new THREE.Vector3(0, 1, 0);
  if (live.length >= 3) {
    // covariance
    let xx = 0, xy = 0, xz = 0, yy = 0, yz = 0, zz = 0;
    for (const p of live) {
      const dx = p.position.x - center.x, dy = p.position.y - center.y, dz = p.position.z - center.z;
      xx += dx * dx; xy += dx * dy; xz += dx * dz; yy += dy * dy; yz += dy * dz; zz += dz * dz;
    }
    // smallest eigenvector of C = largest eigenvector of (trace·I − C); power iteration
    const tr = xx + yy + zz;
    const m = [tr - xx, -xy, -xz, -xy, tr - yy, -yz, -xz, -yz, tr - zz];
    let v = new THREE.Vector3(0.3, 1, 0.2).normalize();
    for (let i = 0; i < 24; i++) {
      const w = new THREE.Vector3(
        m[0] * v.x + m[1] * v.y + m[2] * v.z,
        m[3] * v.x + m[4] * v.y + m[5] * v.z,
        m[6] * v.x + m[7] * v.y + m[8] * v.z,
      );
      if (w.lengthSq() < 1e-12) break;
      v = w.normalize();
    }
    if (v.lengthSq() > 0.5) n = v;
    if (n.y < 0) n.negate(); // keep "up" consistent with the world
  }
  const u = Math.abs(n.y) < 0.9 ? new THREE.Vector3(0, 1, 0).cross(n).normalize() : new THREE.Vector3(1, 0, 0).cross(n).normalize();
  const vv = new THREE.Vector3().crossVectors(n, u).normalize();

  let extent = 20;
  for (const p of live) {
    const d = new THREE.Vector3(p.position.x, p.position.y, p.position.z).sub(center);
    const r = Math.hypot(d.dot(u), d.dot(vv)) + p.radius * 2.5;
    if (r > extent) extent = r;
  }
  return { center, u, v: vv, n, extent: extent + 10 };
}

// ---- sampling -------------------------------------------------------------

interface Grid {
  N: number;
  cell: number;
  pts: Float32Array;     // world positions, 3 per sample
  mag: Float32Array;     // |a|
  t: Float32Array;       // colour ramp 0–1
  phi: Float32Array;     // potential
  ax: Float32Array; ay: Float32Array; az: Float32Array;  // in-plane unit direction of pull
  masked: Uint8Array;    // inside a body
}

function potentialAt(p: Vec3, planets: PlanetState[]): number {
  let phi = 0;
  for (const planet of planets) {
    if (!planet.alive) continue;
    const dx = planet.position.x - p.x, dy = planet.position.y - p.y, dz = planet.position.z - p.z;
    const r = Math.max(Math.sqrt(dx * dx + dy * dy + dz * dz), planet.radius * 0.5);
    phi -= G * planet.mass / r;
  }
  return phi;
}

function sampleGrid(plane: Plane, planets: PlanetState[], N: number): Grid {
  const cell = (2 * plane.extent) / (N - 1);
  const total = N * N;
  const g: Grid = {
    N, cell, pts: new Float32Array(total * 3), mag: new Float32Array(total), t: new Float32Array(total), phi: new Float32Array(total),
    ax: new Float32Array(total), ay: new Float32Array(total), az: new Float32Array(total), masked: new Uint8Array(total),
  };
  const logWeak = Math.log(A_WEAK), logSpan = Math.log(A_STRONG) - logWeak;
  const live = planets.filter(p => p.alive);
  const p: Vec3 = { x: 0, y: 0, z: 0 };
  for (let j = 0; j < N; j++) {
    for (let i = 0; i < N; i++) {
      const k = j * N + i;
      const x = -plane.extent + i * cell, y = -plane.extent + j * cell;
      p.x = plane.center.x + plane.u.x * x + plane.v.x * y;
      p.y = plane.center.y + plane.u.y * x + plane.v.y * y;
      p.z = plane.center.z + plane.u.z * x + plane.v.z * y;
      g.pts[k * 3] = p.x; g.pts[k * 3 + 1] = p.y; g.pts[k * 3 + 2] = p.z;
      for (const pl of live) {
        const dx = p.x - pl.position.x, dy = p.y - pl.position.y, dz = p.z - pl.position.z;
        const rr = pl.radius * 1.05;
        if (dx * dx + dy * dy + dz * dz < rr * rr) { g.masked[k] = 1; break; }
      }
      const a = gravityAccel(p, planets);
      const mag = Math.hypot(a.x, a.y, a.z);
      g.mag[k] = mag;
      g.t[k] = Math.max(0, Math.min(1, (Math.log(Math.max(mag, 1e-9)) - logWeak) / logSpan));
      g.phi[k] = potentialAt(p, planets);
      // in-plane component of the pull
      const dn = a.x * plane.n.x + a.y * plane.n.y + a.z * plane.n.z;
      const tx = a.x - dn * plane.n.x, ty = a.y - dn * plane.n.y, tz = a.z - dn * plane.n.z;
      const tl = Math.hypot(tx, ty, tz) || 1;
      g.ax[k] = tx / tl; g.ay[k] = ty / tl; g.az[k] = tz / tl;
    }
  }
  return g;
}

// ---- layers ---------------------------------------------------------------

const heatVert = `
  attribute vec3 color; attribute float alpha;
  varying vec3 vColor; varying float vAlpha;
  void main() { vColor = color; vAlpha = alpha; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;
const heatFrag = `
  varying vec3 vColor; varying float vAlpha;
  void main() { gl_FragColor = vec4(vColor, vAlpha); }`;

function buildHeat(g: Grid): THREE.Mesh {
  const N = g.N;
  const colors = new Float32Array(N * N * 3);
  const alpha = new Float32Array(N * N);
  for (let k = 0; k < N * N; k++) {
    rampColor(g.t[k], _c);
    colors[k * 3] = _c.r; colors[k * 3 + 1] = _c.g; colors[k * 3 + 2] = _c.b;
    alpha[k] = g.masked[k] ? 0 : HEAT_ALPHA;
  }
  const idx: number[] = [];
  for (let j = 0; j < N - 1; j++) {
    for (let i = 0; i < N - 1; i++) {
      const a = j * N + i, b = a + 1, c = a + N, d = c + 1;
      idx.push(a, c, b, b, c, d);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(g.pts, 3));
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geo.setAttribute('alpha', new THREE.BufferAttribute(alpha, 1));
  geo.setIndex(idx);
  const mat = new THREE.ShaderMaterial({ vertexShader: heatVert, fragmentShader: heatFrag, transparent: true, depthWrite: false, side: THREE.DoubleSide });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.renderOrder = 4;
  mesh.frustumCulled = false;
  return mesh;
}

/** Marching squares on the potential: iso-lines of the combined wells. */
function buildContours(g: Grid): THREE.LineSegments {
  const N = g.N;
  // levels: log-spaced over the unmasked range of −φ
  let lo = Infinity, hi = -Infinity;
  for (let k = 0; k < N * N; k++) if (!g.masked[k]) { const v = -g.phi[k]; if (v < lo) lo = v; if (v > hi) hi = v; }
  const levels: number[] = [];
  if (isFinite(lo) && isFinite(hi) && hi > lo) {
    const a = Math.log(Math.max(lo, 1e-6)), b = Math.log(hi);
    for (let i = 1; i <= CONTOUR_LEVELS; i++) levels.push(-Math.exp(a + (b - a) * (i / (CONTOUR_LEVELS + 1))));
  }
  const verts: number[] = [];
  const P = (k: number, out: number[]) => { out[0] = g.pts[k * 3]; out[1] = g.pts[k * 3 + 1]; out[2] = g.pts[k * 3 + 2]; };
  const pa = [0, 0, 0], pb = [0, 0, 0];
  const lerpPush = (k0: number, k1: number, level: number) => {
    const f0 = g.phi[k0], f1 = g.phi[k1];
    const s = (level - f0) / ((f1 - f0) || 1e-9);
    P(k0, pa); P(k1, pb);
    verts.push(pa[0] + (pb[0] - pa[0]) * s, pa[1] + (pb[1] - pa[1]) * s, pa[2] + (pb[2] - pa[2]) * s);
  };
  for (const level of levels) {
    for (let j = 0; j < N - 1; j++) {
      for (let i = 0; i < N - 1; i++) {
        const k00 = j * N + i, k10 = k00 + 1, k01 = k00 + N, k11 = k01 + 1;
        if (g.masked[k00] || g.masked[k10] || g.masked[k01] || g.masked[k11]) continue;
        const c = (g.phi[k00] > level ? 1 : 0) | (g.phi[k10] > level ? 2 : 0) | (g.phi[k11] > level ? 4 : 0) | (g.phi[k01] > level ? 8 : 0);
        if (c === 0 || c === 15) continue;
        // edges: 0 = bottom (k00–k10), 1 = right (k10–k11), 2 = top (k01–k11), 3 = left (k00–k01)
        const edge = (e: number) => {
          if (e === 0) lerpPush(k00, k10, level);
          else if (e === 1) lerpPush(k10, k11, level);
          else if (e === 2) lerpPush(k01, k11, level);
          else lerpPush(k00, k01, level);
        };
        const seg = (e0: number, e1: number) => { edge(e0); edge(e1); };
        switch (c) {
          case 1: case 14: seg(3, 0); break;
          case 2: case 13: seg(0, 1); break;
          case 3: case 12: seg(3, 1); break;
          case 4: case 11: seg(1, 2); break;
          case 5: seg(3, 2); seg(0, 1); break;
          case 6: case 9: seg(0, 2); break;
          case 7: case 8: seg(3, 2); break;
          case 10: seg(3, 0); seg(1, 2); break;
        }
      }
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(verts), 3));
  const mat = new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: CONTOUR_ALPHA, depthWrite: false });
  const lines = new THREE.LineSegments(geo, mat);
  lines.renderOrder = 5;
  lines.frustumCulled = false;
  return lines;
}

/** Sparse break arrows: in-plane pull direction, length by strength, with a chevron head. */
function buildArrows(g: Grid, plane: Plane, quality: number): THREE.LineSegments {
  const N = g.N;
  const stride = Math.max(2, Math.round(N / (8 + 10 * quality)));
  const verts: number[] = [];
  const cols: number[] = [];
  const push = (x: number, y: number, z: number, c: THREE.Color, b: number) => { verts.push(x, y, z); cols.push(c.r * b, c.g * b, c.b * b); };
  for (let j = stride >> 1; j < N; j += stride) {
    for (let i = stride >> 1; i < N; i += stride) {
      const k = j * N + i;
      if (g.masked[k]) continue;
      const t = g.t[k];
      const len = g.cell * stride * (0.35 + 0.6 * t);
      const ox = g.pts[k * 3], oy = g.pts[k * 3 + 1], oz = g.pts[k * 3 + 2];
      const dx = g.ax[k], dy = g.ay[k], dz = g.az[k];
      const tx = ox + dx * len, ty = oy + dy * len, tz = oz + dz * len;
      rampColor(t, _c);
      // shaft, dim tail → bright tip
      push(ox - dx * len * 0.5, oy - dy * len * 0.5, oz - dz * len * 0.5, _c, 0.45);
      push(tx, ty, tz, _c, 1.0);
      // chevron: rotate the shaft direction ±150° within the plane
      const sx = plane.n.y * dz - plane.n.z * dy, sy = plane.n.z * dx - plane.n.x * dz, sz = plane.n.x * dy - plane.n.y * dx; // n × d
      const h = len * 0.32;
      const ca = Math.cos(2.62), sa = Math.sin(2.62);
      for (const sign of [1, -1]) {
        const hx = dx * ca + sx * sa * sign, hy = dy * ca + sy * sa * sign, hz = dz * ca + sz * sa * sign;
        push(tx, ty, tz, _c, 1.0);
        push(tx + hx * h, ty + hy * h, tz + hz * h, _c, 0.8);
      }
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(verts), 3));
  geo.setAttribute('color', new THREE.BufferAttribute(new Float32Array(cols), 3));
  const mat = new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, opacity: ARROW_ALPHA, depthWrite: false });
  const lines = new THREE.LineSegments(geo, mat);
  lines.renderOrder = 6;
  lines.frustumCulled = false;
  return lines;
}

/** Rings at the calmest pockets: local minima of |a| away from the bodies. */
function buildSaddles(g: Grid, plane: Plane): THREE.Group {
  const N = g.N;
  const group = new THREE.Group();
  const minima: Array<{ k: number; mag: number }> = [];
  for (let j = 1; j < N - 1; j++) {
    for (let i = 1; i < N - 1; i++) {
      const k = j * N + i;
      if (g.masked[k]) continue;
      const m = g.mag[k];
      let isMin = true;
      for (let dj = -1; dj <= 1 && isMin; dj++) {
        for (let di = -1; di <= 1; di++) {
          if (!di && !dj) continue;
          const kk = (j + dj) * N + (i + di);
          if (g.masked[kk] || g.mag[kk] < m) { isMin = false; break; }
        }
      }
      if (isMin) minima.push({ k, mag: m });
    }
  }
  minima.sort((a, b) => a.mag - b.mag);
  const ringGeo = new THREE.RingGeometry(g.cell * 0.9, g.cell * 1.25, 32);
  const ringMat = new THREE.MeshBasicMaterial({ color: FLAT, transparent: true, opacity: 0.85, side: THREE.DoubleSide, depthWrite: false });
  const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), plane.n);
  for (const { k } of minima.slice(0, SADDLE_MARKERS_MAX)) {
    const ring = new THREE.Mesh(ringGeo, ringMat);
    ring.position.set(g.pts[k * 3], g.pts[k * 3 + 1], g.pts[k * 3 + 2]);
    ring.quaternion.copy(q);
    ring.renderOrder = 6;
    group.add(ring);
  }
  return group;
}

// ---- lifecycle ------------------------------------------------------------

function disposeChildren(group: THREE.Group): void {
  group.traverse((child) => {
    if (child instanceof THREE.Mesh || child instanceof THREE.LineSegments) {
      child.geometry.dispose();
      (child.material as THREE.Material).dispose();
    }
  });
  group.clear();
}

function build(field: GravityField, planets: PlanetState[]): void {
  disposeChildren(field.group);
  field.previewLine = null;
  field.previewImpact = null;
  const plane = fitPlane(planets);
  field.plane = plane;
  const N = Math.round(56 + 40 * Math.max(0, Math.min(1, field.quality)));
  const g = sampleGrid(plane, planets, N);
  field.group.add(buildHeat(g));
  field.group.add(buildContours(g));
  field.group.add(buildArrows(g, plane, field.quality));
  field.group.add(buildSaddles(g, plane));
  field.planetKey = planetKeyOf(planets);
}

export function createGravityField(scene: THREE.Scene, planets: PlanetState[], quality = 1): GravityField {
  const field: GravityField = { group: new THREE.Group(), quality, planetKey: '', plane: null, previewLine: null, previewImpact: null };
  build(field, planets);
  scene.add(field.group);
  return field;
}

/** Per frame: rebuild only when the planets changed. Nothing animates. */
export function updateGravityField(field: GravityField, planets: PlanetState[], _dt: number): void {
  if (planetKeyOf(planets) !== field.planetKey) build(field, planets);
}

// ---- aim preview shadow ---------------------------------------------------

const PREVIEW_COLOR_BURN = new THREE.Color(1.0, 0.95, 0.75);
const PREVIEW_COLOR_COAST = new THREE.Color(0.80, 0.95, 1.0);

/**
 * Draw the aim preview flattened onto the map plane — the shot's shadow on the
 * green — brighter during the burn, with a ring at the projected impact.
 * Pass `null` to clear. Drawn without depth testing so the shadow stays
 * readable whether the real path runs above or below the plane.
 */
export function setGravityFieldPreview(field: GravityField, points: Vec3[] | null, burnoutIndex = 0): void {
  if (field.previewLine) { field.group.remove(field.previewLine); field.previewLine.geometry.dispose(); (field.previewLine.material as THREE.Material).dispose(); field.previewLine = null; }
  if (field.previewImpact) { field.group.remove(field.previewImpact); field.previewImpact.geometry.dispose(); (field.previewImpact.material as THREE.Material).dispose(); field.previewImpact = null; }
  const plane = field.plane;
  if (!plane || !points || points.length < 2) return;

  const n = plane.n, c = plane.center;
  const pos = new Float32Array(points.length * 3);
  const col = new Float32Array(points.length * 3);
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const d = (p.x - c.x) * n.x + (p.y - c.y) * n.y + (p.z - c.z) * n.z;
    pos[i * 3] = p.x - d * n.x; pos[i * 3 + 1] = p.y - d * n.y; pos[i * 3 + 2] = p.z - d * n.z;
    const cc = i < burnoutIndex ? PREVIEW_COLOR_BURN : PREVIEW_COLOR_COAST;
    const fade = 1 - 0.45 * (i / (points.length - 1)); // fades toward the impact
    col[i * 3] = cc.r * fade; col[i * 3 + 1] = cc.g * fade; col[i * 3 + 2] = cc.b * fade;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
  const mat = new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.95, depthTest: false, depthWrite: false });
  const line = new THREE.Line(geo, mat);
  line.renderOrder = 8;
  line.frustumCulled = false;
  field.group.add(line);
  field.previewLine = line;

  const last = points[points.length - 1];
  const dl = (last.x - c.x) * n.x + (last.y - c.y) * n.y + (last.z - c.z) * n.z;
  const ringGeo = new THREE.RingGeometry(0.7, 1.0, 32);
  const ringMat = new THREE.MeshBasicMaterial({ color: PREVIEW_COLOR_BURN, transparent: true, opacity: 0.9, side: THREE.DoubleSide, depthTest: false, depthWrite: false });
  const ring = new THREE.Mesh(ringGeo, ringMat);
  ring.position.set(last.x - dl * n.x, last.y - dl * n.y, last.z - dl * n.z);
  ring.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), n);
  ring.renderOrder = 8;
  field.group.add(ring);
  field.previewImpact = ring;
}

export function disposeGravityField(scene: THREE.Scene, field: GravityField): void {
  disposeChildren(field.group);
  scene.remove(field.group);
}
